import { UserPermissions, UserSubscription } from '@creditsights/api-ui-web';
import isEmpty from 'lodash/isEmpty';
import { Icon, ICONS } from '@creditsights/styleguide-web';
import { COMPANY_ROUTES, ROUTES } from '../../Base.constants';
import { LinkGroupRoute } from '../../components/LinkGroup/LinkGroup';
import { IssuerType } from '../../models';
import { IssuerDto } from '../../services/dtos/issuer/issuer.dto';
import { WeightingType } from '../../components/WeightIcon/WeightIcon';
import {
  hasCovenantsPermissions,
  hasNewsPermissions,
  hasNoPermissionToDocketDocument,
  hasNoPermissionToFinancials,
  hasPermissionToAskAnAnalyst,
  hasResearchPermissions,
} from '../../utils/permissionsUtils';

export const getIssuerRoutes = (
  issuerType: IssuerType,
  issuerId: string,
  isCompanyCovered: boolean,
  isFinancialsCovered: boolean,
  isRiskTabVisible: boolean,
  hasDeals: boolean,
  isDocumentsTabVisible: boolean,
  isDocketsTabVisible: boolean,
  isCoveredRPOnly: boolean,
  userPermissions: UserPermissions | undefined,
  isFinancialsTabVisible: boolean
): LinkGroupRoute[] => {
  switch (issuerType) {
    case IssuerType.Company:
      return getCompanyRoutes(
        issuerId,
        isCompanyCovered,
        isFinancialsCovered,
        isRiskTabVisible,
        hasDeals,
        isDocumentsTabVisible,
        isDocketsTabVisible,
        isCoveredRPOnly,
        userPermissions,
        isFinancialsTabVisible
      );
    case IssuerType.Municipal:
      return getMunicipalRoutes();
    default:
      return [];
  }
};

export const getCompanyRoutes = (
  companyId: string,
  isCompanyCovered: boolean,
  isFinancialsCovered: boolean,
  isRiskTabVisible: boolean,
  hasDeals: boolean,
  isDocumentsTabVisible: boolean,
  isDocketsTabVisible: boolean,
  isCoveredRPOnly: boolean,
  userPermissions: UserPermissions | undefined,
  isFinancialsTabVisible: boolean
): LinkGroupRoute[] => {
  const routes: LinkGroupRoute[] = [];

  if (!isCoveredRPOnly) {
    routes.push({
      to: COMPANY_ROUTES.recentlyPublished,
      label: 'Recently Published',
      dataTestId: 'recently-published-tab',
      end: true,
    });
  }
  if (isRiskTabVisible) {
    routes.push({
      to: COMPANY_ROUTES.risk,
      label: 'Risk',
      dataTestId: 'risk-tab',
      end: false,
      icon: !userPermissions?.hasAccessToRP() && (
        <Icon dataTestId="risk-lock-icon" name={ICONS.LockOutline} width={12} height={12} />
      ),
    });
  }
  if (!isCoveredRPOnly) {
    if (hasDeals) {
      routes.push({
        to: COMPANY_ROUTES.deals,
        label: 'Deals Pipeline',
        dataTestId: 'deals-tab',
        end: false,
      });
    }
    if (isCompanyCovered) {
      routes.unshift({
        to: ROUTES.company(companyId),
        label: 'Overview',
        dataTestId: 'overview-tab',
        end: true,
      });
    }
    if (isDocumentsTabVisible) {
      routes.push({
        to: COMPANY_ROUTES.documents,
        label: 'Documents',
        dataTestId: 'documents-tab',
        end: false,
        icon: !isDocumentsTabAccessible(userPermissions) && (
          <Icon dataTestId="documents-lock-icon" name={ICONS.LockOutline} width={12} height={12} />
        ),
      });
    }
    if (isDocketsTabVisible) {
      routes.push({
        to: COMPANY_ROUTES.dockets,
        label: 'Court Dockets',
        dataTestId: 'dockets-tab',
        end: true,
        icon: hasNoPermissionToDocketDocument(userPermissions) && (
          <Icon dataTestId="dockets-lock-icon" name={ICONS.LockOutline} width={12} height={12} />
        ),
      });
    }

    if (isFinancialsCovered && isFinancialsTabVisible) {
      routes.push({
        to: COMPANY_ROUTES.financials,
        label: 'Financials',
        dataTestId: 'financials-tab',
        end: true,
        icon: hasNoPermissionToFinancials(userPermissions) && (
          <Icon dataTestId="financials-lock-icon" name={ICONS.LockOutline} width={12} height={12} />
        ),
      });
    }
  }

  return routes;
};

export const getMunicipalRoutes = (): LinkGroupRoute[] => [
  {
    to: COMPANY_ROUTES.recentlyPublished,
    label: 'Recently Published',
    dataTestId: 'recently-published-tab',
    end: true,
  },
];

export const isCompanyCovered = (company: Partial<IssuerDto> | undefined): boolean => {
  if (!company) {
    return false;
  }
  return [
    company.csCoverage,
    company.lfiCoverage,
    company.crCoverage,
    company.financialsCoverage,
    isRpCovered(company),
  ].some((c) => c);
};
export const isCompanyCoveredRPOnly = (company: Partial<IssuerDto> | undefined): boolean => {
  if (!company) {
    return false;
  }
  return (
    ![company.csCoverage, company.lfiCoverage, company.crCoverage, company.financialsCoverage].some((c) => c) &&
    isRpCovered(company)
  );
};

export const isFinancialsCovered = (company: IssuerDto | undefined): boolean => company?.financialsCoverage || false;
export const isRpCovered = (company: Partial<IssuerDto> | undefined): boolean => !!company?.riskProductsCoverage;

export const isDocumentsTabAccessible = (userPermissions?: UserPermissions): boolean => {
  if (userPermissions) {
    return (
      [UserSubscription.CS_CR_LFI, UserSubscription.CR, UserSubscription.CS_CR, UserSubscription.CR_LFI].includes(
        userPermissions.userSubscription
      ) || userPermissions.permissions.includes('ui:show-company-docs')
    );
  }

  return false;
};

export const isCompanyRoute = (issuerId: string, pathname: string): boolean => pathname === ROUTES.company(issuerId);

export const isDealsRoute = (issuerId: string, pathname: string): boolean =>
  pathname.startsWith(`${ROUTES.company(issuerId)}/${COMPANY_ROUTES.deals}`);

export const isDocketsRoute = (issuerId: string, pathname: string): boolean =>
  pathname.startsWith(`${ROUTES.company(issuerId)}/${COMPANY_ROUTES.dockets}`);

export const hideCSView = (company: IssuerDto | undefined): boolean =>
  company?.latestRec?.weighting === WeightingType.NoRecommendation && isEmpty(company?.latestRec?.comment);

type Modes = 'CR' | 'CS' | 'LFI';

export const isAAAButtonVisible = (
  company: IssuerDto | undefined,
  userPermissions: UserPermissions | undefined
): boolean => {
  if (company && userPermissions) {
    if (hasPermissionToAskAnAnalyst(userPermissions)) {
      const companyCoverage: Modes[] = [];
      const userModes: Modes[] = [];

      // checks Company for coverages and pushes appropriate identifications into an array
      if (company.crCoverage) {
        companyCoverage.push('CR');
      }
      if (company.csCoverage) {
        companyCoverage.push('CS');
      }
      if (company.lfiCoverage) {
        companyCoverage.push('LFI');
      }

      // checks User for permissions and pushes appropriate identifications into an array
      if (hasCovenantsPermissions(userPermissions)) {
        userModes.push('CR');
      }
      if (hasResearchPermissions(userPermissions)) {
        userModes.push('CS');
      }
      if (hasNewsPermissions(userPermissions)) {
        userModes.push('LFI');
      }

      // if Company only has LFI coverage, don't show AAA
      if (companyCoverage.includes('LFI') && companyCoverage.length === 1) {
        return false;
      }

      return companyCoverage.some((coverage) => userModes.includes(coverage));
    }
  }
  return false;
};
